import React from 'react';
import PropTypes from 'prop-types';
import {
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  BanknotesIcon,
  ArrowDownCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  PlusIcon,
  ClipboardIcon as HeroClipboardIcon,
  ArrowPathIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';

// ICONS now map to functions so callers can pass a custom className (eg. to remove right margin)
const ICONS = {
  // add `text-current` so icons inherit the button's text color (important for solid buttons)
  plus: (cls = 'h-5 w-5 mr-2 text-current') => <PlusIcon className={cls} aria-hidden="true" />,
  edit: (cls = 'h-5 w-5 mr-2 text-current') => <PencilSquareIcon className={cls} aria-hidden="true" />,
  delete: (cls = 'h-5 w-5 mr-2') => <TrashIcon className={cls + ' text-red-600'} aria-hidden="true" />,
  download_v2: (cls = 'h-5 w-5 mr-2 text-current') => <ArrowDownCircleIcon className={cls} aria-hidden="true" />,
  download: (cls = 'h-5 w-5 mr-2 text-current') => <ArrowDownTrayIcon className={cls} aria-hidden="true" />,
  search: (cls = 'h-5 w-5 mr-2 text-current') => <MagnifyingGlassIcon className={cls} aria-hidden="true" />,
  envelope: (cls = 'h-5 w-5 mr-2 text-current') => <EnvelopeIcon className={cls} aria-hidden="true" />,
  payment: (cls = 'h-5 w-5 mr-2 text-current') => <BanknotesIcon className={cls} aria-hidden="true" />,
  clipboard: (cls = 'h-5 w-5 mr-2 text-current') => <HeroClipboardIcon className={cls} aria-hidden="true" />,
  refresh: (cls = 'h-5 w-5 mr-2 text-current') => <ArrowPathIcon className={cls} aria-hidden="true" />,
  table: (cls = 'h-5 w-5 mr-2 text-current') => <TableCellsIcon className={cls} aria-hidden="true" />,
};

const SIZE_CLASSES = {
  small: 'px-3 py-1.5 text-sm h-8',
  medium: 'px-4 py-2 text-sm h-10',
  mediumSelectAligned: 'px-4 h-10 text-sm',
  mediumSend: 'px-3 py-2 h-10 text-sm',
  smallIcon: 'px-2 py-1 h-8 text-sm',
  large: 'px-6 py-3 text-base',
};

export const CustomButton = ({
                               icon,
                               caption,
                               type = 'button',
                               disabled = false,
                               onClick,
                               size = 'medium',
                               solid = false,
                               asLink = false,
                               href = '#',
                               className = '',
                               iconNoMargin = false,
                               ...rest
                             }) => {
  // Primary CTA uses the Predico brand teal (#05788a) in both modes, matching
  // the active-page chip in <Pagination />. White text reads cleanly on it in
  // light and dark themes — no need to invert.
  const solidClasses = `border border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/50`;
  const outlineClasses = `bg-white text-slate-900 border border-slate-900 hover:bg-slate-100 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-300 dark:hover:bg-gray-700`;

  // If the caller requested a solid button or this is the mediumSend size, use solid styling by default
  const useSolid = solid || size === 'mediumSend';

  const baseClass = `inline-flex items-center justify-center rounded-md font-medium transition 
  focus:outline-none focus:ring-0 active:ring-0 active:outline-none
  ${useSolid ? solidClasses : outlineClasses}
  ${SIZE_CLASSES[size]} ${className}`;

  // Icon inherits the parent's text color (brand-700 / brand-300 on solid
  // buttons, slate-900 / gray-100 on outline buttons).
  const defaultIconCls = iconNoMargin ? 'h-5 w-5' : 'h-5 w-5 mr-2';
  const iconCls = defaultIconCls;
  const iconElement = icon ? (ICONS[icon] ? ICONS[icon](iconCls) : null) : null;

  const content = (
    <>
      {iconElement}
      <span>{caption}</span>
    </>
  );

  if (asLink) {
    return (
      <a
        href={href}
        onClick={onClick}
        className={`${baseClass} ${disabled ? 'pointer-events-none opacity-50' : ''}`}
        {...rest}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={`${baseClass} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      {...rest}
    >
      {content}
    </button>
  );
};

CustomButton.propTypes = {
  icon: PropTypes.oneOf([
    'plus',
    'edit',
    'delete',
    'envelope',
    'download',
    'download_v2',
    'search',
    'payment',
    'clipboard',
    'refresh',
    'table',
  ]),
  caption: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  size: PropTypes.oneOf(['small', 'medium', 'large', 'mediumSelectAligned', 'mediumSend', 'smallIcon']),
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  disabled: PropTypes.bool,
  solid: PropTypes.bool,
  asLink: PropTypes.bool,
  href: PropTypes.string,
  className: PropTypes.string,
  iconNoMargin: PropTypes.bool,
};

export default CustomButton;
